/* eslint-disable no-await-in-loop */
import { AxiosResponse } from 'axios';
import axios, { API_URL } from '../../services/axios';
import { Passport } from '../../types/passport';
import { AppThunk } from '../store';
import { setProfile, updateUserPassport } from '../user';
import {
  addApplicant,
  deleteAddress,
  removeApplicant,
  setLoading,
  setPassport,
  updateApplicant,
  updateApplicantAddress,
} from './preapproval.slice';

const DELAY_TIME = process?.env?.JEST_WORKER_ID ? 0 : 100;

export const getPassport =
  (passportId: string) =>
  async (dispatch: any): Promise<AxiosResponse<Passport> | null> => {
    try {
      const result = await axios<Passport>(
        {
          method: 'GET',
          url: `${API_URL}/mortgages/passport/${passportId}`,
        },
        dispatch,
      );
      await dispatch(setPassport(result.data));
      await dispatch(setLoading(false));
      return result;
    } catch (err: any) {
      return null;
    }
  };

type CreateApplicant = (passportId: string) => AppThunk;
export const createApplicant: CreateApplicant = (passportId: string) => async (dispatch) => {
  const result = await axios(
    {
      method: 'POST',
      url: `${API_URL}/mortgages/passport/${passportId}/applicant`,
    },
    dispatch,
  );

  await dispatch(addApplicant(result.data));

  return result;
};

export const createApplicants = (numberOfApplicants: number, passportId: string) => async (dispatch: any) => {
  // Need to disable await in loop rule, due to the async nature https://eslint.org/docs/latest/rules/no-await-in-loop
  // the previous solution of using promises was calling each item of an array concurrently
  for (let i = 1; i <= numberOfApplicants; i += 1) {
    await dispatch(createApplicant(passportId));
    // add arbitrary delay just to ensure that applicant has been added before moving onto the next one
    // initial issue was that it was calling the endpoint too fast again for dual applicants
    if (numberOfApplicants > 1)
      await new Promise((resolve) => {
        setTimeout(resolve, DELAY_TIME);
      });
  }

  return true;
};

// once a passport has been created, we patch the website user with the id.
type CreatePassport = (numberOfApplicants: number) => AppThunk<Promise<Passport>>;

export const createPassport: CreatePassport = (numberOfApplicants: number) => async (dispatch: any) => {
  const result = await axios<Passport>(
    {
      method: 'POST',
      url: `${API_URL}/mortgages/passport`,
    },
    dispatch,
  );

  await dispatch(updateUserPassport({ passportId: result.data.id }));
  await dispatch(createApplicants(numberOfApplicants, result.data.id));
  await dispatch(setPassport(result.data));

  return result.data;
};

type DeleteApplicant = (passportId: string, applicantIndex: number) => AppThunk<Promise<boolean>>;
export const deleteApplicant: DeleteApplicant = (passportId: string, applicantIndex: number) => async (dispatch) => {
  await axios(
    {
      method: 'DELETE',
      url: `${API_URL}/mortgages/passport/${passportId}/applicant/${applicantIndex}`,
    },
    dispatch,
  );
  dispatch(removeApplicant({ applicantIndex }));

  return true;
};

export const patchPassport = (passportId: string, payload: any) => async (dispatch: any) => {
  // if you need to add try/catch here, know it will affect the try/catch on pre-approval steps
  const result = await axios(
    {
      method: 'PATCH',
      url: `${API_URL}/mortgages/passport/${passportId}`,
      data: payload,
    },
    dispatch,
  );

  dispatch(setPassport(result.data));

  return result;
};

export const patchApplicant = (passportId: string, applicantIndex: number, payload: any) => async (dispatch: any) => {
  // if you need to add try/catch here, know it will affect the try/catch on pre-approval steps
  const result = await axios(
    {
      method: 'PATCH',
      url: `${API_URL}/mortgages/passport/${passportId}/applicant/${applicantIndex + 1}`,
      data: payload,
    },
    dispatch,
  );

  dispatch(
    updateApplicant({
      applicantIndex,
      applicant: result.data,
    }),
  );

  return result;
};

export const createApplicantAddress = (passportId: string, applicantIndex: number) => async (dispatch: any) => {
  try {
    const result = await axios(
      {
        method: 'POST',
        url: `${API_URL}/mortgages/passport/${passportId}/applicant/${applicantIndex + 1}/address`,
      },
      dispatch,
    );
    return result;
  } catch (err: any) {
    return null;
  }
};

export const patchApplicantAddress =
  (passportId: string, applicantIndex: number, addressNum: number, payload: any) => async (dispatch: any) => {
    // if you need to add try/catch here, know it will affect the try/catch on pre-approval steps
    const patchAppResult = await axios(
      {
        method: 'PATCH',
        url: `${API_URL}/mortgages/passport/${passportId}/applicant/${applicantIndex + 1}/address/${addressNum}`,
        data: payload,
      },
      dispatch,
    );
    await dispatch(
      updateApplicantAddress({
        applicantIndex,
        orderIndex: addressNum,
        address: patchAppResult.data,
      }),
    );
    // await dispatch(getPassport(passportId));
    return patchAppResult;
  };

export const createAndPatchAddress =
  (passportId: string, applicantIndex: number, values: any) => async (dispatch: any) => {
    // if you need to add try/catch here, know it will affect the try/catch on pre-approval steps
    const createAddressResult = await dispatch(createApplicantAddress(passportId, applicantIndex));

    await dispatch(patchApplicantAddress(passportId, applicantIndex, createAddressResult.data.orderIndex, values));
    return createAddressResult;
  };

export const deleteApplicantAddress =
  (passportId: string, applicantIndex: number, orderIndex: number) => async (dispatch: any) => {
    try {
      dispatch(
        deleteAddress({
          applicantIndex,
          orderIndex,
        }),
      );

      return await axios(
        {
          method: 'DELETE',
          url: `${API_URL}/mortgages/passport/${passportId}/applicant/${applicantIndex + 1}/address/${orderIndex}`,
        },
        dispatch,
      );
    } catch (err: any) {
      return null;
    }
  };

export const operationCheck = (passportId: string, type: string) => async (dispatch: any) => {
  try {
    const eligibilityResult = await axios<Passport>(
      {
        method: 'POST',
        url: `${API_URL}/mortgages/passport/${passportId}/operation`,
        data: {
          type,
        },
      },

      dispatch,
    );
    return eligibilityResult;
  } catch (err: any) {
    return null;
  }
};

type RefreshPassport = (passportId: string) => AppThunk<Promise<Boolean>>;

export const refreshPassport: RefreshPassport = (passportId) => async (dispatch, getState) => {
  const { user } = getState();

  try {
    const result = await axios<Passport>(
      {
        method: 'POST',
        url: `${API_URL}/mortgages/passport/${passportId}/refresh`,
      },
      dispatch,
    );

    await dispatch(updateUserPassport({ passportId: result.data.id }));
    await dispatch(setPassport(result.data));

    // set user profile to updated profile with currentPassportId set new passport id
    if (user.profile) {
      const profile = {
        ...user.profile,
        currentPassportId: result.data.id || null,
      };

      await dispatch(setProfile(profile));
    }

    return true;
  } catch (err: any) {
    return false;
  }
};
